import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import ChromeFoldContext from '../layouts/ChromeFoldContext';
import useIsPhone, { isPhoneWidth } from './useIsPhone';

export { PHONE_MAX_WIDTH } from './useIsPhone';

export interface UseFoldOnSelectOptions {
  // Whether this surface has a picker worth folding. With one background set
  // there is nothing to collapse, so the step lands as it always has and the
  // chrome stays full height.
  foldByDefault: boolean;
}

export interface UseFoldOnSelectResult {
  folded: boolean;
  fold: () => void;
  unfold: () => void;
  reset: () => void;
  gridRef: React.RefObject<HTMLDivElement>;
}

// ENG-2551: the folded category picker on phones. Below PHONE_MAX_WIDTH a
// surface with more than one background set LANDS folded — the picker renders
// collapsed and V3Layout slims the fixed chrome — so the thumbnails own the
// viewport from the first paint instead of only after a tap. fold() re-applies
// that on a selection and scrolls gridRef to the top so the chosen set's first
// row is in view; unfold() is the explicit route back to the stacked list
// (chips only); reset() returns to the default for the current viewport.
// Desktop widths never fold.
const useFoldOnSelect = ({
  foldByDefault,
}: UseFoldOnSelectOptions): UseFoldOnSelectResult => {
  const isPhone = useIsPhone();
  const foldedByDefault = isPhone && foldByDefault;
  const [folded, setFoldedLocal] = useState(foldedByDefault);
  const { setFolded: setChromeFolded } = useContext(ChromeFoldContext);
  const gridRef = useRef<HTMLDivElement>(null);

  // The default is derived, not remembered: it applies on mount and again
  // whenever the viewport crosses the phone breakpoint in either direction. A
  // fold entered on a phone must not survive a rotation to desktop width, and
  // rotating back must land folded again. No scroll here — on mount the grid
  // is already where the customer is looking.
  useEffect(() => {
    setFoldedLocal(foldedByDefault);
    setChromeFolded(foldedByDefault);
  }, [foldedByDefault, setChromeFolded]);

  const fold = useCallback(() => {
    if (!isPhoneWidth()) return;
    setFoldedLocal(true);
    setChromeFolded(true);
    // Measure after the folded layout has rendered, not before.
    window.requestAnimationFrame(() => {
      if (gridRef.current && gridRef.current.scrollIntoView) {
        gridRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }, [setChromeFolded]);

  const unfold = useCallback(() => {
    setFoldedLocal(false);
    setChromeFolded(false);
  }, [setChromeFolded]);

  const reset = useCallback(() => {
    setFoldedLocal(foldedByDefault);
    setChromeFolded(foldedByDefault);
  }, [foldedByDefault, setChromeFolded]);

  // Never leave the layout chrome stuck slim after the step unmounts.
  useEffect(() => () => setChromeFolded(false), [setChromeFolded]);

  return { folded, fold, unfold, reset, gridRef };
};

export default useFoldOnSelect;
